import { Injectable } from '@angular/core';
import { Actions, createEffect, EffectsModule, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UserStoreService } from '@polaris-customerweb/sharedcomponents.core';
import {
    concatMap,
    mergeMap,
    pairwise,
    startWith,
    withLatestFrom,
} from 'rxjs/operators';
import { selectRideGroupRiders } from 'src/app/components/ride-group/ride-group.reducer';
import { AppState } from 'src/app/models/app-state';
import { RideGroupMessage } from 'src/app/models/ride-group-message';
import { RideGroupService } from 'src/app/services/ride-group.service';
import * as GroupRideMessageActions from './group-ride-message.actions';
import { selectGroupRideMessages } from './group-ride.selectors';

@Injectable()
export class GroupRideMessageEffects {
    mergeAlertsIntoMessages = createEffect(() => {
        return this.actions$.pipe(
            ofType(GroupRideMessageActions.SetGroupRideAlerts),
            startWith({ alerts: [] }),
            pairwise(),
            withLatestFrom(this.store.select(selectRideGroupRiders)),
            withLatestFrom(this.store.select(selectGroupRideMessages)),
            concatMap(([[[prev, curr], riders], messages]) => {
                const newMsgs: RideGroupMessage[] =
                    this.rideGroupService.getNewAlertMessages(
                        prev.alerts,
                        curr.alerts,
                        riders
                    );
                // update existing alert messages to be canceled if necessary
                for (const msg of newMsgs.filter((x) => x.isCanceled)) {
                    const newlyCanceledAlert = messages.find(
                        (x) =>
                            x.isAlert &&
                            x.riderFeatureId === msg.riderFeatureId &&
                            !x.isCanceled
                    );
                    if (newlyCanceledAlert) {
                        newlyCanceledAlert.isCanceled = true;
                    }
                }
                // update messages state and set alerts if new ones have come in
                if (newMsgs.some((x) => !x.isCanceled)) {
                    return [
                        GroupRideMessageActions.SetHasNewAlerts({
                            hasNewAlerts: true,
                        }),
                        GroupRideMessageActions.SetGroupRideMessages({
                            messages: [...messages, ...newMsgs],
                        }),
                    ];
                } else {
                    return [
                        GroupRideMessageActions.SetGroupRideMessages({
                            messages: [...messages, ...newMsgs],
                        }),
                    ];
                }
            })
        );
    });

    handleMessageAlerts = createEffect(() => {
        return this.actions$.pipe(
            ofType(GroupRideMessageActions.AppendGroupRideMessages),
            withLatestFrom(this.store.select(selectGroupRideMessages)),
            mergeMap(([action, oldMessages]) => {
                const messages = this.rideGroupService.dedupeMessages(
                    oldMessages,
                    action.messages
                );
                const time = this.storeService.get<number>(
                    'recentMessageTimestamp'
                ) as number;
                const newTime =
                    messages[action.messages?.length - 1]?.timestamp;
                return [
                    GroupRideMessageActions.SetGroupRideMessages({
                        messages,
                    }),
                    newTime > time
                        ? GroupRideMessageActions.SetHasNewMessages({
                              hasNewMessages: true,
                          })
                        : GroupRideMessageActions.Noop(),
                ];
            })
        );
    });

    constructor(
        private actions$: Actions,
        private storeService: UserStoreService,
        private rideGroupService: RideGroupService,
        private store: Store<AppState>
    ) {}
}

export const groupRideMessageEffectsModule = EffectsModule.forFeature([
    GroupRideMessageEffects,
]);
